import { addDays, format } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import { CheckCircle2, Circle, Clock, MapPin, Phone, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AdditionalInfoSection } from "../../components/tasks/AdditionalInfoSection";
import { GET_READY_TASK_CATALOG } from "../../domain/getReadyTasks/catalog";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./BookAppointmentPage.module.css";

interface DateOption {
	id: string;
	dateLabel: string;
	timeLabel: string;
}

const DATE_TIMES = [
	{ daysOffset: 1, time: "10:00 AM - 11:00 AM" },
	{ daysOffset: 3, time: "2:30 PM - 3:30 PM" },
	{ daysOffset: 7, time: "9:00 AM - 10:00 AM" },
];

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

function buildRelativeDates(locale: string): DateOption[] {
	const today = new Date();
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	return DATE_TIMES.map(({ daysOffset, time }, i) => ({
		id: `opt${i + 1}`,
		dateLabel: format(addDays(today, daysOffset), "EEEE, MMMM d, yyyy", {
			locale: dateFnsLocale,
		}),
		timeLabel: time,
	}));
}

interface RequiredDocRowProps {
	label: string;
	sub: string;
	completed: boolean;
}

function RequiredDocRow({ label, sub, completed }: RequiredDocRowProps) {
	return (
		<div
			className={`${styles.reqDocRow} ${completed ? styles.reqDocRowDone : styles.reqDocRowPending}`}
		>
			{completed ? (
				<CheckCircle2
					className={styles.reqDocIcon}
					aria-hidden="true"
					strokeWidth={1.5}
				/>
			) : (
				<Circle
					className={`${styles.reqDocIcon} ${styles.reqDocIconPending}`}
					aria-hidden="true"
					strokeWidth={1.5}
				/>
			)}
			<div className={styles.reqDocContent}>
				<span className={styles.reqDocLabel}>{label}</span>
				<span className={styles.reqDocSub}>{sub}</span>
			</div>
		</div>
	);
}

export function BookAppointmentPage() {
	const { t, locale } = useClientTranslations("getReady");
	const navigate = useNavigate();
	const { logGetReady } = useAnalytics();
	const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
	const [notes, setNotes] = useState("");

	const dateOptions = useMemo(() => buildRelativeDates(locale), [locale]);

	const handleClose = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const handleBook = useCallback(() => {
		try {
			logGetReady("book_appointment_pressed", {
				has_date_selected: selectedDateId !== null,
			});
		} catch {
			// non-blocking
		}
		try {
			toast(t("task.cta.comingSoon"));
		} catch {
			// non-blocking
		}
	}, [logGetReady, selectedDateId, t]);

	const p = (key: string) => t(`taskPages.bookAppointment.${key}`);

	return (
		<div className={styles.page}>
			{/* Header */}
			<div className={styles.header}>
				<div className={styles.headerText}>
					<h1 className={styles.headerTitle}>{p("title")}</h1>
					<p className={styles.headerSubtitle}>{p("subtitle")}</p>
				</div>
				<button
					type="button"
					className={styles.closeButton}
					onClick={handleClose}
					aria-label={p("close")}
				>
					<X className={styles.closeIcon} aria-hidden="true" />
				</button>
			</div>

			<div className={styles.additionalInfoWrapper}>
				<AdditionalInfoSection
					taskKey="book_appointment"
					domainColor={
						GET_READY_TASK_CATALOG.book_appointment.domainColor ?? "#C1C1C1"
					}
				/>
			</div>

			<div className={styles.body}>
				{/* Required Before Booking */}
				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>
						{p("sections.required.title")}
					</h2>
					<div className={styles.reqDocList}>
						<RequiredDocRow
							label={p("sections.required.items.bloodWork")}
							sub={p("sections.required.items.bloodWork.sub")}
							completed={false}
						/>
						<RequiredDocRow
							label={p("sections.required.items.questionnaire")}
							sub={p("sections.required.items.questionnaire.sub")}
							completed
						/>
						<RequiredDocRow
							label={p("sections.required.items.insurance")}
							sub={p("sections.required.items.insurance.sub")}
							completed
						/>
					</div>
				</section>

				{/* Select Your Preferred Date */}
				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>{p("sections.date.title")}</h2>
					<div className={styles.dateList}>
						{dateOptions.map((opt) => (
							<button
								key={opt.id}
								type="button"
								className={`${styles.dateCard} ${selectedDateId === opt.id ? styles.dateCardSelected : ""}`}
								onClick={() => setSelectedDateId(opt.id)}
								aria-pressed={selectedDateId === opt.id}
							>
								<span
									className={`${styles.dateCheckbox} ${selectedDateId === opt.id ? styles.dateCheckboxSelected : ""}`}
									aria-hidden="true"
								/>
								<span className={styles.dateCardContent}>
									<span className={styles.dateCardDate}>{opt.dateLabel}</span>
									<span className={styles.dateCardTime}>{opt.timeLabel}</span>
								</span>
							</button>
						))}
					</div>
				</section>

				{/* Appointment Details */}
				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>{p("sections.details.title")}</h2>
					<div className={styles.detailsCard}>
						<div className={styles.detailRow}>
							<Clock className={styles.detailIcon} aria-hidden="true" />
							<div className={styles.detailText}>
								<span className={styles.detailLabel}>
									{p("sections.details.duration.label")}
								</span>
								<span className={styles.detailSub}>
									{p("sections.details.duration.value")}
								</span>
							</div>
						</div>
						<div className={styles.detailRow}>
							<MapPin className={styles.detailIcon} aria-hidden="true" />
							<div className={styles.detailText}>
								<span className={styles.detailLabel}>
									{p("sections.details.location.label")}
								</span>
								<span className={styles.detailSub}>
									{p("sections.details.location.value")}
								</span>
							</div>
						</div>
						<div className={styles.detailRow}>
							<Phone className={styles.detailIcon} aria-hidden="true" />
							<div className={styles.detailText}>
								<span className={styles.detailLabel}>
									{p("sections.details.contact.label")}
								</span>
								<span className={styles.detailSub}>
									{p("sections.details.contact.value")}
								</span>
							</div>
						</div>
					</div>
				</section>

				{/* Notes */}
				<section className={styles.section}>
					<label htmlFor="book-notes" className={styles.notesLabel}>
						{p("sections.notes.label")}
					</label>
					<textarea
						id="book-notes"
						className={styles.notesTextarea}
						placeholder={p("sections.notes.placeholder")}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						rows={4}
					/>
				</section>

				{/* CTA */}
				<button type="button" className={styles.ctaButton} onClick={handleBook}>
					{p("cta")}
				</button>
			</div>
		</div>
	);
}
